import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, Bell, HelpCircle, Mail, Menu, MessageSquare, Phone, Search, X } from "lucide-react";
import { useTourStarter } from "@/shared/contexts/CoachMarksRegistryContext";
import { RunningIcon } from "@/assets/icons/RunningIcon";
import { useTranslation } from "react-i18next";
import LocaleSelector from "@/shared/components/LocaleSelector";
import { SwitchVersionIcon } from "@/assets/icons/SwitchVersionIcon";
import { useClientSearch } from "@/shared/hooks/useClientSearch";
import { useNotificationCount } from "@/shared/hooks/useNotificationCount";
import { useScheduledRunsCount } from "@/shared/hooks/useScheduledRunsCount";
import { useUnreadMessagesCount } from "@/shared/hooks/useUnreadMessagesCount";
import { useEmergencyCount } from "@/shared/hooks/useEmergencyCount";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { PERMISSIONS } from "@/shared/constants/permissions";
import CurrentTime from "./CurrentTime";
import { useServerPhase } from "@/shared/hooks/useServerPhase";
import { useTenant } from "@/shared/hooks/useTenant";
import { useAuth } from "@/shared/providers/AuthProvider";
import { useRuntimeConfigContext } from "@/shared/providers/RuntimeConfigProvider";
import { useUpdateUser } from "@/features/system/hooks/useUpdateUser";
import { useToast } from "@/shared/contexts/ToastContext";
import ChangePasswordModal from "@/features/system/components/userManagement/ChangePasswordModal";
import { ViewSettingsModal } from "@/shared/components/viewSettings";
import SendFeedbackModal from "./SendFeedbackModal";
import { useSendFeedback } from "./hooks/useSendFeedback";
import logoWhite from "@/assets/ecolaneWebWhite.svg";
import transitLogo from "@/assets/transit_logo.svg";
import NavMenu from "./NavMenu";
import MobileNavDrawer from "./MobileNavDrawer";
import AppearanceToggle from "./AppearanceToggle";
import { getNavItems, menuRoutePrefixes } from "./navConfig.ts";
import { getAccessibleNavConfig } from "@/shared/utils/nav.utils.ts";
import { env } from "@/config/env";
import { NavItem } from "./types";
import "./Navbar.css";

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Global coach-marks Tour button — `useTourStarter()` returns the
  // currently-mounted page's `start` callback, or `null` when no page
  // has registered one. The button below renders disabled in the null
  // case so the affordance is always present but only lights up where
  // a tour is actually available.
  const tourStart = useTourStarter();
  // Server-time phase drives an ambient day/night background tint on the
  // navbar chrome — see `Navbar.css` for the visual overlay layer, and
  // `useServerPhase` for the rationale on why this lives separate from
  // the AppearanceProvider's light/dark theme.
  const { isDay: isServerDay } = useServerPhase();
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // `mobileMenuButtonRef` is used to restore focus to the hamburger
  // when the drawer closes — keyboard users would otherwise lose
  // focus context after the drawer unmounts.
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { clients, isLoading } = useClientSearch(searchTerm, isSearchOpen);
  const { notificationCount } = useNotificationCount();
  const { scheduledRunsCount } = useScheduledRunsCount();
  const { unreadMessagesCount } = useUnreadMessagesCount();
  const { emergencyCount } = useEmergencyCount();
  const { hasPermission } = usePermissions();
  const canViewAlerts = hasPermission(PERMISSIONS.ALERT_VIEW);
  const { tenantId } = useTenant();
  const { user, permissions, logout } = useAuth();
  const { config, getConfigValue } = useRuntimeConfigContext();
  const { showToast } = useToast();
  const { mutate: updateUser } = useUpdateUser();
  const { mutateAsync: submitFeedback } = useSendFeedback();
  const [isChangePasswordOpen, setChangePasswordOpen] = useState(false);
  const [isViewSettingsOpen, setViewSettingsOpen] = useState(false);
  const [isSendFeedbackOpen, setSendFeedbackOpen] = useState(false);
  const currentUsername = user?.login ?? user?.name ?? "User";

  // Determine which logo to use based on tenant
  const logo = tenantId === "metro-transit" ? transitLogo : logoWhite;

  const navItems = React.useMemo(() => getNavItems(config, user), [config, user, i18n.language]);

  const accessibleNavItems = React.useMemo(() => {
    if (!permissions) {
      return {
        operations: [],
        administration: [],
        tools: [],
        system: [],
        user: navItems.user,
      };
    }
    return {
      operations: getAccessibleNavConfig(navItems.operations, permissions),
      administration: getAccessibleNavConfig(navItems.administration, permissions),
      tools: getAccessibleNavConfig(navItems.tools, permissions),
      system: getAccessibleNavConfig(navItems.system, permissions),
      user: navItems.user,
    };
  }, [navItems, permissions]);

  const handleMenuToggle = (menuId: string) => {
    setOpenMenu(openMenu === menuId ? null : menuId);
  };

  const handleMenuClose = () => {
    setOpenMenu(null);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    setSearchTerm("");
  };

  const handleClientClick = (clientId: number) => {
    handleSearchClose();
    navigate(`/admin/clients/${clientId}`);
  };

  const handleChangePassword = async (password: string) => {
    if (!user) return;
    return new Promise<void>((resolve, reject) => {
      updateUser(
        {
          userId: Number(user.id),
          payload: {
            id: Number(user.id),
            login: currentUsername,
            password: password,
          },
          // ECOPD-14897: This is always the current user changing their own
          // password. Pass reloginAs so useUpdateUser.onSuccess re-authenticates
          // with the new password before query refetches fire — keeping the user
          // logged in without requiring them to visit /login.
          reloginAs: { login: currentUsername, password },
        },
        {
          onSuccess: () => {
            showToast(t("system.userManagement.users.changePasswordSuccess"), "success");
            resolve();
          },
          onError: (error) => {
            showToast(error.message, "error");
            reject(error);
          },
        }
      );
    });
  };

  const handleSendFeedback = async ({
    comment,
    attachments,
  }: {
    comment: string;
    attachments: File[];
  }) => {
    try {
      await submitFeedback({
        comment,
        attachments,
        url: window.location.href,
        category: "general",
      });
      showToast(t("navbar.user.sendFeedback") + " – " + t("common.changesSavedSuccess"), "success");
    } catch (error) {
      const errorMessage = (error as Error)?.message || t("common.error");
      showToast(errorMessage, "error");
      throw error;
    }
  };

  const normalizePath = (path: string) => {
    if (!path) return "/";
    const normalized = path.split(/[?#]/)[0].replace(/\/+$/, "").toLowerCase();
    return normalized || "/";
  };

  const isPathMatch = (targetPath: string, currentPath: string) => {
    if (!targetPath || targetPath === "#") {
      return false;
    }

    const normalizedTarget = normalizePath(targetPath);
    const normalizedCurrent = normalizePath(currentPath);

    if (
      normalizedCurrent === normalizedTarget ||
      normalizedCurrent.startsWith(`${normalizedTarget}/`)
    ) {
      return true;
    }

    const embeddedIndex = normalizedCurrent.indexOf(normalizedTarget);
    if (embeddedIndex === -1) {
      return false;
    }

    const hasSegmentBoundaryBefore =
      embeddedIndex === 0 || normalizedCurrent[embeddedIndex - 1] === "/";
    const afterIndex = embeddedIndex + normalizedTarget.length;
    const hasSegmentBoundaryAfter =
      afterIndex === normalizedCurrent.length || normalizedCurrent[afterIndex] === "/";

    return hasSegmentBoundaryBefore && hasSegmentBoundaryAfter;
  };

  const hasActiveRoute = (items: NavItem[], currentPath: string): boolean => {
    return items.some((item) => {
      if (isPathMatch(item.to, currentPath)) {
        return true;
      }

      if (!item.submenu) {
        return false;
      }

      return hasActiveRoute(item.submenu, currentPath);
    });
  };

  const isMenuPrefixActive = (menuId: string, currentPath: string): boolean => {
    const prefixes = menuRoutePrefixes[menuId] ?? [];
    return prefixes.some((prefix) => isPathMatch(prefix, currentPath));
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") {
        return;
      }

      if (isSearchOpen) {
        handleSearchClose();
      }

      if (openMenu) {
        handleMenuClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isSearchOpen, openMenu]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".navbar-menu-container")) {
        handleMenuClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className="navbar-root sticky top-0 h-16 z-[210] bg-header"
      data-server-phase={isServerDay ? "day" : "night"}
      aria-label="Main navigation"
    >
      <div className="max-w-full mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-2">
          <div className="flex items-center gap-3 md:gap-8 min-w-0">
            {/* Hamburger — only visible below `md`. Toggles a drawer
                that lists all 4 navigation menus expanded (Operations,
                Admin, Tools, System). On md+ the desktop menubar
                replaces it entirely. The button toggles the drawer in
                both directions so `aria-expanded` is honoured by
                keyboard users. */}
            <button
              ref={mobileMenuButtonRef}
              type="button"
              className="md:hidden text-on-primary p-1 -ml-1"
              aria-label={isMobileNavOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isMobileNavOpen}
              aria-controls="mobile-nav-drawer"
              onClick={() => {
                handleMenuClose();
                setIsMobileNavOpen((prev) => !prev);
              }}
              data-testid="navbar-mobile-menu-toggle"
            >
              <Menu size={22} aria-hidden="true" />
            </button>
            <Link to="/" onClick={handleMenuClose} aria-label="Go to home page">
              <img src={logo} alt="Logo" className="h-6 w-auto object-contain mr-3" />
            </Link>
            <div className="hidden md:flex items-center gap-2" role="menubar">
              <NavMenu
                title={t("navbar.menu.operations")}
                menuId="operations"
                items={accessibleNavItems.operations}
                openMenu={openMenu}
                isRouteActive={
                  location.state?.from === 'operations' ||
                  (location.state?.from !== 'client' && (
                    hasActiveRoute(accessibleNavItems.operations, location.pathname) ||
                    isMenuPrefixActive("operations", location.pathname)
                  ))
                }
                onToggle={handleMenuToggle}
                onClose={handleMenuClose}
              />
              <NavMenu
                title={t("navbar.menu.administration")}
                menuId="admin"
                items={accessibleNavItems.administration}
                openMenu={openMenu}
                isRouteActive={
                  location.state?.from !== 'operations' && (
                    (location.state?.from === 'client' && (
                      hasActiveRoute(accessibleNavItems.operations, location.pathname) ||
                      isMenuPrefixActive("operations", location.pathname)
                    )) ||
                    hasActiveRoute(accessibleNavItems.administration, location.pathname) ||
                    isMenuPrefixActive("admin", location.pathname)
                  )
                }
                onToggle={handleMenuToggle}
                onClose={handleMenuClose}
              />
              <NavMenu
                title={t("navbar.menu.tools")}
                menuId="tools"
                items={accessibleNavItems.tools}
                openMenu={openMenu}
                isRouteActive={
                  hasActiveRoute(accessibleNavItems.tools, location.pathname) ||
                  isMenuPrefixActive("tools", location.pathname)
                }
                onToggle={handleMenuToggle}
                onClose={handleMenuClose}
              />
              <NavMenu
                title={t("navbar.menu.system")}
                menuId="system"
                items={accessibleNavItems.system}
                openMenu={openMenu}
                isRouteActive={
                  hasActiveRoute(accessibleNavItems.system, location.pathname) ||
                  isMenuPrefixActive("system", location.pathname)
                }
                onToggle={handleMenuToggle}
                onClose={handleMenuClose}
              />
            </div>
          </div>
          {/* Right side — gaps tighten on narrow viewports, and the
              "Switch to older version" + locale selector hide below `md`
              (they're non-essential on phone/tablet). The four count
              badges (scheduled / emergency / alerts / messages) stay
              visible because dispatchers need them at a glance. */}
          <div className="flex items-center gap-3 md:gap-6 lg:gap-8 right-navbar">
            <CurrentTime />
            <AppearanceToggle />
            {/* Global Coach Marks Tour button. Disabled when no page
                has registered a tour starter via `useRegisterTour`. The
                affordance is always rendered so users learn the icon
                placement; the disabled state is visually muted so it
                doesn't compete with active controls. */}
            <div
              className={
                tourStart ? "navbar-tooltip navbar-tooltip-bottom" : undefined
              }
            >
              <button
                type="button"
                onClick={tourStart ?? undefined}
                disabled={!tourStart}
                aria-label={t("fleetDashboard.coachMarks.triggerAria", { defaultValue: "Take a tour" })}
                className="text-on-primary disabled:opacity-30 disabled:cursor-not-allowed enabled:hover:text-info transition-colors"
                data-testid="global-coach-marks-trigger"
                data-enabled={tourStart ? "true" : "false"}
              >
                <HelpCircle size={16} aria-hidden="true" />
              </button>
              {tourStart && (
                <span className="navbar-tooltip-text" role="tooltip">
                  {t("fleetDashboard.coachMarks.triggerTooltip", { defaultValue: "Take a tour" })}
                </span>
              )}
            </div>
            <button
              className="text-on-primary"
              aria-label="Search"
              onClick={() => setIsSearchOpen(true)}
            >
              <Search size={16} aria-hidden="true" />
            </button>
            <div className="navbar-tooltip navbar-tooltip-bottom hidden md:block">
              <button
                className="text-on-primary flex items-center gap-1"
                aria-label="Switch to older version"
                onClick={() => {
                  const primaryHost =
                    getConfigValue<string>("supervisor.frontend.primaryhost") || env.OLD_APP_URL;
                  // Ensure it has protocol
                  const url = primaryHost.startsWith("http")
                    ? `${primaryHost}drt`
                    : `https://${primaryHost}drt`;
                  window.location.href = url;
                }}
              >
                <SwitchVersionIcon aria-hidden="true" />
              </button>
              <span className="navbar-tooltip-text" role="tooltip">
                Switch to older version
              </span>
            </div>

            <div className="hidden md:block">
              <LocaleSelector />
            </div>
            {/* Order mirrors legacy app: runs → emergency → alerts → messages */}
            {scheduledRunsCount > 0 && (
              <div className="navbar-tooltip navbar-tooltip-bottom">
                <button
                  className="text-on-primary flex items-center gap-1"
                  aria-label={t("navbar.scheduledRuns", {
                    count: scheduledRunsCount,
                    defaultValue: "Scheduled runs: {{count}}",
                  })}
                  onClick={() =>
                    navigate(
                      "/system/batch-jobs?q.run_asap=1&q.enabled=no_filter&q.range_default_enum=no_filter"
                    )
                  }
                  data-testid="navbar-scheduled-runs-button"
                >
                  <div className="relative inline-flex items-center">
                    <RunningIcon width={16} height={16} aria-hidden="true" />
                    <span
                      className="absolute -top-2 -right-3 min-w-[16px] h-[16px] px-1 bg-info text-white text-[10px] font-semibold leading-[16px] text-center rounded-full pointer-events-none"
                      data-testid="navbar-scheduled-runs-count"
                    >
                      {scheduledRunsCount > 99 ? "99+" : scheduledRunsCount}
                    </span>
                  </div>
                </button>
                <span className="navbar-tooltip-text" role="tooltip">
                  {t("navbar.scheduledTasksTooltip", {
                    count: scheduledRunsCount,
                    defaultValue: "Scheduled tasks: {{count}}",
                  })}
                </span>
              </div>
            )}
            {emergencyCount > 0 && (
              <div className="navbar-tooltip navbar-tooltip-bottom">
                <button
                  className="text-on-primary flex items-center gap-1"
                  aria-label={t("navbar.emergency", {
                    count: emergencyCount,
                    defaultValue: "Emergency alerts: {{count}}",
                  })}
                  onClick={() =>
                    navigate("/operations/alerts?keep=1&q.alert_group=emerg&q.status=unresolved")
                  }
                  data-testid="navbar-emergency-button"
                >
                  <div className="relative inline-block">
                    <Bell size={16} aria-hidden="true" />
                    <span
                      className="absolute -top-2 -right-3 min-w-[16px] h-[16px] px-1 bg-error text-white text-[10px] font-semibold leading-[16px] text-center rounded-full pointer-events-none"
                      data-testid="navbar-emergency-count"
                    >
                      {emergencyCount > 99 ? "99+" : emergencyCount}
                    </span>
                  </div>
                </button>
                <span className="navbar-tooltip-text" role="tooltip">
                  {t("navbar.emergencyTooltip", {
                    count: emergencyCount,
                    defaultValue: "Emergency: {{count}}",
                  })}
                </span>
              </div>
            )}
            <div className="navbar-tooltip navbar-tooltip-bottom">
              <button
                className="text-on-primary flex items-center gap-1"
                aria-label={t("navbar.notifications", {
                  count: notificationCount,
                  defaultValue: "Notifications: {{count}} unread",
                })}
                onClick={() =>
                  navigate(
                    "/operations/alerts?keep=1&q.range_default_enum=today&q.status=unresolved"
                  )
                }
              >
                <div className="relative inline-block">
                  <AlertTriangle size={16} aria-hidden="true" />
                  {notificationCount > 0 && (
                    <span
                      className="absolute -top-2 -right-3 min-w-[16px] h-[16px] px-1 bg-warning text-white text-[10px] font-semibold leading-[16px] text-center rounded-full pointer-events-none"
                      data-testid="navbar-notification-count"
                    >
                      {notificationCount > 99 ? "99+" : notificationCount}
                    </span>
                  )}
                </div>
              </button>
              <span className="navbar-tooltip-text" role="tooltip">
                {t("navbar.alertsTooltip", {
                  count: notificationCount,
                  defaultValue: "Alerts: {{count}}",
                })}
              </span>
            </div>
            {canViewAlerts && unreadMessagesCount > 0 && (
              <div className="navbar-tooltip navbar-tooltip-bottom">
                <button
                  className="text-on-primary flex items-center gap-1"
                  aria-label={t("navbar.unreadMessages", {
                    count: unreadMessagesCount,
                    defaultValue: "Unread messages: {{count}}",
                  })}
                  onClick={() =>
                    navigate(
                      "/operations/messages?keep=1&q.handle_status=unread&q.range_default_enum=today"
                    )
                  }
                  data-testid="navbar-unread-messages-button"
                >
                  <div className="relative inline-block">
                    <MessageSquare size={16} aria-hidden="true" />
                    <span
                      className="absolute -top-2 -right-3 min-w-[16px] h-[16px] px-1 bg-info text-white text-[10px] font-semibold leading-[16px] text-center rounded-full pointer-events-none"
                      data-testid="navbar-unread-messages-count"
                    >
                      {unreadMessagesCount > 99 ? "99+" : unreadMessagesCount}
                    </span>
                  </div>
                </button>
                <span className="navbar-tooltip-text" role="tooltip">
                  {t("navbar.messagesTooltip", {
                    count: unreadMessagesCount,
                    defaultValue: "Messages: {{count}}",
                  })}
                </span>
              </div>
            )}
            <div className="flex gap-2 items-center">
              <NavMenu
                title={currentUsername}
                menuId="userMenu"
                items={accessibleNavItems.user.map((item) => {
                  if (item.key === "user-feedback") {
                    return {
                      ...item,
                      onClick: () => {
                        setSendFeedbackOpen(true);
                        handleMenuClose();
                      },
                    };
                  }
                  if (item.key === "change-password") {
                    return { ...item, onClick: () => setChangePasswordOpen(true) };
                  }
                  if (item.key === "view-settings") {
                    return { ...item, onClick: () => setViewSettingsOpen(true) };
                  }
                  if (item.key === "user-signout") {
                    return {
                      ...item,
                      onClick: () => {
                        handleMenuClose();
                        logout();
                      },
                    };
                  }
                  if (item.key === "user-version-info") {
                    const versionNumber = getConfigValue<string>("system.version.number") ?? "";
                    return { ...item, version: versionNumber };
                  }
                  return item;
                })}
                isUserMenu
                openMenu={openMenu}
                isRouteActive={false}
                onToggle={handleMenuToggle}
                onClose={handleMenuClose}
              />
            </div>
          </div>
        </div>
      </div>

      <MobileNavDrawer
        isOpen={isMobileNavOpen}
        onClose={() => {
          setIsMobileNavOpen(false);
          // Restore keyboard focus to the hamburger so screen-reader
          // users don't lose context when the drawer unmounts.
          mobileMenuButtonRef.current?.focus();
        }}
        sections={[
          { id: "operations",     title: t("navbar.menu.operations"),     items: accessibleNavItems.operations },
          { id: "administration", title: t("navbar.menu.administration"), items: accessibleNavItems.administration },
          { id: "tools",          title: t("navbar.menu.tools"),          items: accessibleNavItems.tools },
          { id: "system",         title: t("navbar.menu.system"),         items: accessibleNavItems.system },
        ]}
      />

      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        username={currentUsername}
        onChangePassword={handleChangePassword}
      />

      <ViewSettingsModal isOpen={isViewSettingsOpen} onClose={() => setViewSettingsOpen(false)} />

      <SendFeedbackModal
        isOpen={isSendFeedbackOpen}
        onClose={() => setSendFeedbackOpen(false)}
        onSubmit={handleSendFeedback}
      />

      {isSearchOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20"
          onClick={handleSearchClose}
        >
          <div
            className="bg-surface rounded-lg shadow-lg w-full max-w-2xl mx-4 max-h-[600px] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 p-4 border-b border-border">
              <Search size={20} className="text-text-muted" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search for clients by name, phone, or email..."
                className="flex-1 bg-transparent border-none outline-none text-text placeholder:text-text-muted"
                autoFocus
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                onClick={handleSearchClose}
                className="text-text-muted hover:text-text"
                aria-label="Close search"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {!searchTerm || searchTerm.length < 2 ? (
                <div className="p-6 text-center">
                  <div className="flex flex-col items-center justify-center gap-4">
                    <Search size={64} className="text-text-muted/30" aria-hidden="true" />
                    <div>
                      <h3 className="text-lg font-medium text-text mb-2">Search for clients</h3>
                      <p className="text-sm text-text-muted">
                        Type a name, phone number, or email to find clients
                      </p>
                    </div>
                  </div>
                </div>
              ) : isLoading ? (
                <div className="p-6 text-center">
                  <p className="text-sm text-text-muted">Searching...</p>
                </div>
              ) : clients.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-sm text-text-muted">No clients found</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {clients.map((client) => {
                    const getInitials = (name: string) => {
                      const parts = name.trim().split(" ");
                      if (parts.length >= 2) {
                        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
                      }
                      return name.substring(0, 2).toUpperCase();
                    };

                    const initials = getInitials(client.name);
                    const phone = client.phone_mobile || client.phone_landline;

                    return (
                      <button
                        key={client.id}
                        onClick={() => handleClientClick(client.id)}
                        className="w-full px-4 py-3 hover:bg-surface-alt transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm bg-primary">
                            {initials}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-text mb-0.5">{client.name}</div>
                            <div className="flex items-center gap-3 text-text-muted text-sm">
                            <div className="flex items-center gap-1">
                              <Phone size={14} aria-hidden="true" />
                              <span>{client.phone_mobile || client.phone_landline || "N/A"}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Mail size={14} aria-hidden="true" />
                              <span className="max-w-[280px] truncate">
                                {client.email || "N/A"}
                              </span>
                            </div></div>
                          </div>

                          {/* <div className="flex items-center gap-3 text-text-muted text-sm">
                            <div className="flex items-center gap-1">
                              <Phone size={14} aria-hidden="true" />
                              <span>{client.phone_mobile || client.phone_landline || "N/A"}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Mail size={14} aria-hidden="true" />
                              <span className="max-w-[200px] truncate">``
                                {client.email || "N/A"}
                              </span>
                            </div>
                          </div> */}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
